import { couponFormFactory } from "./coupon_form_factory";
import {
  getFormattedCurrencyValueFromEl,
  getValueWithoutFormat,
  formatterCurrency,
  getLowerCaseInputValue,
} from "./formatters";
import {
  isPJ,
  doesNotHaveValue,
  parseFetchResponse,
  checkoutCustomerFormHelpers,
  disableSubmitButtonElement,
  setElementHiddenClass,
  disableRequiredInputField,
} from "./helper_functions";
import { debounced } from "./debounced";
import { GtmFactory, CHECKOUT_STEP, PAYMENT_TYPE } from "./gtm_script";
import { setupMPMTabs } from "./payments/malga/mpm_tabs";
import { setupPixPayment } from "./payments/malga/pix";
import { setupNupayPayment } from "./payments/malga/nupay";
import { addCorrId, tapAndLoadCorrId } from './utils/correlation_id_store';
import { setupClientForm } from "./utils/form/client/client_form";
import { setupCreditCardForm } from "./utils/form/payment/cc_form";
import { getPaymentInfoHelpers } from "./utils/form/payment/payment_form";
import { setupProductForm } from "./utils/form/product/product_form";
import { useRecaptcha } from "./utils/recaptcha_wrapper";
import { webhook } from "./webhook_service";
import { getUTMParams } from './utils/cookie_manager';

export async function setupMalgaPayment(MPMInstance) {
  let paymentIntentId = null;

  // eslint-disable-next-line eqeqeq
  const tokenize_cvv = document.getElementsByName("save_card").item(0)?.value == "true";

  // Product details
  const productIdHiddenInput = document.getElementById("product_id");
  const productNameHiddenInput = document.getElementById("product_name");
  const productUnitAmountHiddenInput = document.getElementById("unit-amount") || document.getElementById("payment-total");
  const paymentTotalSpan = document.getElementById("payment-total");
  const pixTotalAmountSpan = document.getElementById('pix_total_amount');

  // Defines PF or PJ from backend
  const formClientType = document.getElementById("client_payment_client_type");
  const pjMode = isPJ(formClientType.value);

  // form
  const formElement = document.getElementById("client_payment_form");
  const formNameInput = document.getElementById("client_payment_name");
  const formEmailInput = document.getElementById("client_payment_email");
  const formDocumentInput = document.getElementById("client_payment_document");
  const isForeignerInput = document.getElementById("is_foreigner");
  const formPhoneNumberInput = document.getElementById("client_payment_phone_number");
  const formZipCodeInput = document.getElementById("client_payment_zip_code");
  const whatsappAcceptElement = document.getElementById("whatsapp_checkbox");
  // CNPJ registration
  const stateRegistrationInput = formElement.querySelector("input#client_payment_state_registration");
  const municipalRegistrationInput = formElement.querySelector("input#client_payment_municipal_registration");

  const productProviderHiddenInput = document.getElementById("provider");
  // const clientCreatedAtHiddenInput = document.getElementById("client_created_at");
  const formCouponIdHiddenInput = document.getElementById("client_payment_coupon_id");
  const formAmountOffHiddenInput = document.getElementById("client_payment_amount_off");
  const formPercentOffHiddenInput = document.getElementById("client_payment_percent_off");
  const formDiscountAmountHiddenInput = document.getElementById("client_payment_discount_amount");
  const formAmountHiddenInput = document.getElementById("client_payment_amount");
  const formTotalAmountHiddenInput = document.getElementById("client_payment_total_amount");
  const formPixAmountHiddenInput = document.getElementById("client_payment_pix_amount");
  const formTotalPixAmountHiddenInput = document.getElementById("client_payment_total_pix_amount");
  const formCountInstallmentsHiddenInput = document.getElementById("client_payment_installments_count");
  const formFixedInstallmentHiddenInput = document.getElementById("client_payment_fixed_installments");

  const stateRegistrationAlertMessageElement = document.querySelector("#error_registration");
  const formDocumentAlertMessageSpan = document.querySelector("#document_validation");
  const formZipCodeAlertMessageSpan = document.querySelector("#zip_code_validation");

  // UI Form elements
  const whatsappLabelElement = document.getElementById("whatsapp_label");
  const documentLabelElement = document.getElementById("document_label");
  const zipCodeLabelElement = document.getElementById("zip_code_label");
  const showInstallmentsSpan = document.getElementById("show-installments");

  /** @type {HTMLFormElement} */
  const paymentInputsForm = document.getElementById("payment-input-form");

  /** @type {HTMLButtonElement} */
  const submitButtonElement = document.querySelector("#submit");

  /** @type {HTMLDivElement} */
  const buttonLoadingSpinner = submitButtonElement.querySelector("#pay-now-button-spinner");
  /** @type {HTMLSpanElement} */
  const payNowButtonLabel = submitButtonElement.querySelector("#pay-now-button-label");

  const formHelpers = checkoutCustomerFormHelpers({
    alertMessageDocumentElement: formDocumentAlertMessageSpan,
    documentLabelElement,
    formDocumentElement: formDocumentInput,
    formPhoneNumberElement: formPhoneNumberInput,
    formClientType,
    isForeignerElement: isForeignerInput,
    submitButtonElement,
    whatsappAcceptElement,
    whatsappLabelElement,
    alertMessageZipCodeElement: formZipCodeAlertMessageSpan,
    formZipCodeElement: formZipCodeInput,
    zipCodeLabelElement
  });

  const clientForm = setupClientForm(formHelpers);

  /** @type {HTMLOptionElement[]} */
  const installmentsOptionsElements = Array.from(
    document.querySelectorAll(
      "form#client_payment_form select[name=\"client_payment[installments_count]\"] > option"
    )
  );

  const bumpForm = {
    listeners: [],
    getActiveEl() {
      return document.getElementById("order-bump-active");
    },
    getValueEl() {
      return document.getElementById("order-bump-value");
    },
    enabled() {
      return Boolean(this.getActiveEl() && this.getValueEl());
    },
    active() {
      const el = this.getActiveEl();
      return Boolean(el?.checked || false);
    },
    getValue() {
      const el = this.getValueEl();
      const value = Number(el?.value);
      return Number.isNaN(value) || !value || value < 0 ? 0 : value;
    },
    triggerChange() {
      const state = {
        active: this.active(),
        activeEl: this.getActiveEl(),
        value: this.getValue(),
      };
      this.listeners.forEach((listener) => listener(state));
    },
    addChangeListener(listener) {
      if (!this.listeners.includes(listener)) {
        this.listeners.push(listener);
      }
    }
  }

  bumpForm.getActiveEl()?.addEventListener("change", function bumpOrderActiveHandler() {
    bumpForm.triggerChange();
  });

  // Coupon Form init
  const couponElements = window.coupon_accepted && {
    formAmountHiddenInput,
    couponInput: document.getElementById("coupon_id"),
    discountAmountSpan: document.getElementById("discount-amount"),
    paymentTotalSpan,
    clearCouponButton: document.getElementById("clear_coupon"),
    errorSpanContainer: document.getElementById("coupon-container")?.parentElement,
    submitCouponButton: document.getElementById("submit_coupon"),
    pixTotalAmountSpan,
    emailInput: formEmailInput,
    submitFormButton: submitButtonElement
  };

  const couponForm = window.coupon_accepted && couponFormFactory({
    elements: couponElements,
    productId: productIdHiddenInput.value
  });

  /**
   * Update installments options or span
   *
   * @param {string} amount numeric value
   * @param {boolean} [fixedInstallments=false] flag
   */
  const updateInstallments = (amount, fixedInstallments = false) => {
    if (fixedInstallments) {
      // operates over single span element
      const numericValue = Number(formCountInstallmentsHiddenInput.value);
      const formattedInstallmentValue = formatterCurrency.format(
        Number(amount) / numericValue
      );
      showInstallmentsSpan.innerText = `${numericValue}x ${formattedInstallmentValue} sem juros`;
    } else {
      // operate over options elements
      installmentsOptionsElements.forEach(function (item) {
        const numericValue = Number(item.value);
        const formattedInstallmentValue = formatterCurrency.format(
          Number(amount) / numericValue
        );
        item.innerText = `${numericValue}x ${formattedInstallmentValue} sem juros`;
      });
    }
  };

  if (window.coupon_accepted) {
    let timeoutRef = null;
    if (couponForm && !couponForm.fixedCoupon) {
      if (couponForm) {
        /**
         * Handles changes in coupon values
         *
         * @param {{
         *   amount: string
         *   amountOff: number
         *   coupon: string
         *   discountAmount: number
         *   discountedAmount: number
         *   formattedDiscount: string
         *   formattedDiscountedAmount: string
         *   percentOff: string
         *   pixAmount: number
         * } | null} coupon
         * @param {boolean=} clear
         */
        const couponChangeHandler = (coupon, clear = false) => {
          const bumpValue = bumpForm.active() ? bumpForm.getValue() : 0;
          const rawOriginalAmount = (Number(formAmountHiddenInput.value) + bumpValue) / 100;
          const originalAmount = formatterCurrency.format(rawOriginalAmount);

          if (clear) {
            formCouponIdHiddenInput.value = "";
            formTotalAmountHiddenInput.value = formAmountHiddenInput.value;
            formTotalPixAmountHiddenInput.value = formPixAmountHiddenInput.value;
            formDiscountAmountHiddenInput.value = "";
            formAmountOffHiddenInput.value = "";
            formPercentOffHiddenInput.value = "";

            couponElements.paymentTotalSpan.innerText = originalAmount;
            couponElements.discountAmountSpan.innerText = "";

            updateInstallments(rawOriginalAmount, formFixedInstallmentHiddenInput?.value === "true");
            return;
          } else if (coupon === null && formCouponIdHiddenInput.value) {
            if (timeoutRef !== null) {
              clearTimeout(timeoutRef);
            }
            timeoutRef = setTimeout(() => {
              couponForm.removeErrorSpan();
            }, 3000);
            return;
          } else if (coupon === null) {
            couponForm.appendErrorSpan();
            return;
          }

          couponForm.removeErrorSpan();

          formAmountOffHiddenInput.value = coupon.amountOff;
          formCouponIdHiddenInput.value = coupon.coupon;
          formDiscountAmountHiddenInput.value = coupon.discountAmount;
          formTotalAmountHiddenInput.value = coupon.discountedAmount;
          formPercentOffHiddenInput.value = coupon.percentOff;
          formTotalPixAmountHiddenInput.value = coupon.pixAmount;

          couponElements.paymentTotalSpan.innerText = bumpValue
            ? formatterCurrency.format((coupon.discountedAmount + bumpValue) / 100)
            : coupon.formattedDiscountedAmount;
          couponElements.discountAmountSpan.innerText = coupon.formattedDiscount;

          updateInstallments(
            (coupon.discountedAmount + bumpValue) / 100,
            formFixedInstallmentHiddenInput?.value === "true",
          );
        };
        couponForm.addCouponChangeListener(couponChangeHandler);
      }
    }
  }
  // END of Coupon Form

  if (bumpForm.getActiveEl()) {
    const paymentTotalSpan = document.getElementById("payment-total");
    bumpForm.addChangeListener(function orderBumpCardTotalListener({ active, value }) {
      const bumpValue = active ? value : 0;
      const totalWithoutBump = Number(formTotalAmountHiddenInput.value);
      const totalWithBump = (totalWithoutBump + bumpValue) / 100;

      paymentTotalSpan.innerText = formatterCurrency.format(totalWithBump);
      updateInstallments(totalWithBump, formFixedInstallmentHiddenInput?.value === "true");
    });
  }

  /**
   * Show a spinner on payment submission
   *
   * @param {boolean} isLoading
   */
  const setLoading = function (isLoading) {
    // Disable the button and show the spinner
    disableSubmitButtonElement(submitButtonElement, isLoading);
    MPMInstance?.setButtonsDisabled(isLoading);

    setElementHiddenClass(buttonLoadingSpinner, !isLoading);
    setElementHiddenClass(payNowButtonLabel, isLoading);
  };

  // Show the customer the error from Stripe if their card fails to charge
  const showPaymentError = function (errorMsgText) {
    setLoading(false);
    const errorMsg = document.querySelector("#card-error");
    errorMsg.textContent = errorMsgText;
  };

  function sendCardErrorToWebhook(errorCode, error) {
    webhook.sendPost("/webhooks", {
      error_level: "tokenization",
      error_code: errorCode,
      error_contents: error.toString(),
      payment_intent_id: paymentIntentId,
      product_name: productNameHiddenInput.value,
      product_id: productIdHiddenInput.value,
      coupon_id: couponElements?.couponInput?.value
    }, {
      "Content-Type": "application/json",
      "X-Payment-Error": "tokenization"
    });
  }

  // Get payment information from form
  const getPaymentInformation = () => ({
    coupon_amount_off: getFormattedCurrencyValueFromEl(formAmountOffHiddenInput),
    discount_amount: getFormattedCurrencyValueFromEl(formDiscountAmountHiddenInput),
    subtotal_amount: productUnitAmountHiddenInput?.innerText,
    total_amount: getFormattedCurrencyValueFromEl(formTotalAmountHiddenInput),
    amount: getFormattedCurrencyValueFromEl(formAmountHiddenInput),
    coupon_id: formCouponIdHiddenInput.value,
    coupon_percent_off: formPercentOffHiddenInput.value,
    provider: productProviderHiddenInput.value,
    product_name: productNameHiddenInput.value,
    product_id: productIdHiddenInput.value,
    installments_count: formCountInstallmentsHiddenInput.value
  });

  // Get client payment information from form
  const getClientPaymentFormData = (paymentMethodId, cvvToken) => ({
    name: formNameInput.value,
    email: formEmailInput.value,
    document: formDocumentInput.value,
    phone_number: formPhoneNumberInput.value,
    zip_code: formZipCodeInput?.value ?? "",
    coupon_id: formCouponIdHiddenInput.value,
    amount_off: formAmountOffHiddenInput.value,
    percent_off: formPercentOffHiddenInput.value,
    discount_amount: formDiscountAmountHiddenInput.value,
    amount: (getValueWithoutFormat(formAmountHiddenInput.value) / 100.0).toFixed(2),
    total_amount: (getValueWithoutFormat(formTotalAmountHiddenInput.value) / 100.0).toFixed(2),
    product_id: productIdHiddenInput.value,
    whatsapp_accept: whatsappAcceptElement.checked,
    installments_count: formCountInstallmentsHiddenInput.value,
    payment_method_id: cvvToken ? `${paymentMethodId}:${cvvToken}` : paymentMethodId,
    payment_intent_id: paymentIntentId,
    is_foreigner: isForeignerInput?.checked,
    provider: productProviderHiddenInput.value,
    is_pj: formHelpers.pjMode,
  });

  const productForm = setupProductForm();

  const paymentHelpers = getPaymentInfoHelpers();

  const gtm = GtmFactory();
  gtm.sendBeginCheckout({
    productId: productIdHiddenInput.value,
    productName: productNameHiddenInput.value,
    price: formTotalAmountHiddenInput.value / 100,
    coupon: formCouponIdHiddenInput.value,
    discount: formDiscountAmountHiddenInput.value / 100,
    fullName: formNameInput.value,
    email: formEmailInput.value,
    phone: formPhoneNumberInput.value
  });

  const creditCardForm = setupCreditCardForm(paymentInputsForm);

  /**
   * Sets inputs blur event to send partial filled form data to backend as a paymentIntent
   */
  const createPaymentIntentOnDB = async () => {
    if (formEmailInput.value.trim().length) {
      const url = window.PM3.paymentIntentsPath;
      const headers = addCorrId(new Headers({ "Content-Type": "application/json" }));
      
      const body = {
        payment_intent: {
          id: paymentIntentId,
          name: formNameInput.value,
          email: getLowerCaseInputValue(formEmailInput),
          product_name: productNameHiddenInput.value,
          product_id: productIdHiddenInput.value,
          total_amount: formTotalAmountHiddenInput.value,
          phone_number: formPhoneNumberInput.value,
          utm: getUTMParams()
        }
      };
      
      const response = await webhook.sendPost(url, body, headers);
      tapAndLoadCorrId(response);
      if (response.ok) {
        paymentIntentId = response.body.payment_intent.id;
      }
    }
  };

  // Sets email field to trigger a Create Payment Intent on lost of focus
  formEmailInput.addEventListener("blur", () => {
    if (formEmailInput.value.trim().length) createPaymentIntentOnDB();
  });

  // Sets phone number field to trigger a Create Payment Intent on lost of focus
  formPhoneNumberInput.addEventListener("blur", () => {
    if (formPhoneNumberInput.value.trim().length) createPaymentIntentOnDB();
  });

  // Shows a success message when the payment is complete and redirect
  const showOrderCompleteAndRedirect = () => {
    showPaymentError("");
    const redirectURL = document.getElementById("typ_url").value;
    const spinnerLoading = document.getElementById("spinner-loading");

    disableSubmitButtonElement(submitButtonElement);

    setTimeout(() => {
      window.parent.location.replace(redirectURL);
    }, 2000);

    spinnerLoading.classList.remove("hidden");
    payNowButtonLabel.innerText = "Pagamento feito com sucesso!";
  };

  /**
   * Submit the form to creates payment intent
   *
   * @param {string} paymentMethodId from the tokenization service
   */
  const fetchClientPayment = (paymentMethodId, cvvToken) => {
    const authenticityTokenElement = getAuthenticityTokenElement();

    const body = {
      authenticity_token: authenticityTokenElement.value,
      order_bump_active: bumpForm.active(),
      client_payment: getClientPaymentFormData(
        paymentMethodId,
        cvvToken
      )
    };


    fetch("/client_payments", {
      method: "POST",
      headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
      body: JSON.stringify(body)
    })
      .then(tapAndLoadCorrId)
      .then(parseFetchResponse)
      .then(async (clientPaymentResponse) => {
        if ("error_message" in clientPaymentResponse.body) {
          console.error({ clientPaymentResponse });
          if (clientPaymentResponse.body?.error?.status == "failed") {
            if (clientPaymentResponse.body.error.statusReason) throw new Error(`reason:${clientPaymentResponse.body.error.statusReason}`);
          }
          throw new Error(clientPaymentResponse.body.error_message);
        }

        if (clientPaymentResponse.status === 200) {
          // The payment had an error!
          throw new Error(clientPaymentResponse.body?.error_message ?? clientPaymentResponse.body?.error ?? "Unknown failure to register client payment");
        }

        // The payment succeeded!
        const { product_id, coupon_id, stripe_confirm_payment_data, id: transactionId } = clientPaymentResponse.body;
        const revenue = stripe_confirm_payment_data.amount / 100;
        const amount = clientPaymentResponse.body.amount / 100;

        const gtmPurchaseEventData = {
          transactionId, // Transaction ID. Required for purchases and refunds.
          coupon: coupon_id,
          totalAmount: revenue,
          productId: product_id,
          productName: stripe_confirm_payment_data.metadata.product_name,
          price: revenue,
          discount: amount - revenue,
          fullName: formNameInput.value,
          email: formEmailInput.value,
          phone: formPhoneNumberInput.value
        };
        const webhooks = [
          gtm.sendEvent(CHECKOUT_STEP.purchase, gtmPurchaseEventData, PAYMENT_TYPE.card), // not webhook but async analytics
        ];

        await Promise.allSettled(webhooks);

        showOrderCompleteAndRedirect();
      })
      .catch((error) => {
        if ("stack" in error) {
          console.error("Failed to create client_payment", error.stack);
        } else {
          console.trace("Failed to create client_payment", error);
        }
        setLoading(false);

        const defaultErrorMessage = "Ocorreu um erro. Verifique os dados preenchidos e tente novamente em alguns minutos";
        if (error.message && /^reason:.+/.test(error.message)) {
          showPaymentError(error.message.split(':')[1])
        } else if (error.message) {
          switch (error.message) {
            case "Invalid CVV":
              showPaymentError("CVV de cartão de crédito inválido");
              break;
            case "expired_card":
              showPaymentError("Cartão de crédito expirado");
            default:
              showPaymentError(defaultErrorMessage);
          }
        } else {
          showPaymentError(defaultErrorMessage);
        }
      });
  };

  const baseGatewayURL = window.PM3.malgaApiUrl;

  async function getMalgaToken() {
    const authenticity_token = getAuthenticityTokenElement().value;
    const gr_token = await useRecaptcha('malga_token');
    const response = await fetch('/client_payments/malga_token', {
      method: 'POST',
      headers: addCorrId(new Headers({
        'Content-Type': 'application/json'
      })),
      body: JSON.stringify({
        gr_action: 'malga_token',
        gr_token,
        authenticity_token,
        product_id: productIdHiddenInput.value,
      }),
    });
    tapAndLoadCorrId(response);
    if (!response.ok) {
      return response.text().then((text) => {
        throw new Error(text);
      });
    }
    return response.json(); // Returns { client_id, client_token }
  }


  // Creates payment method
  const createPaymentMethodResponse = async () => {
    setLoading(true);

    const malga = await getMalgaToken();
    const cardNumber = creditCardForm.creditCardNumber.value;
    const cardExpirationDate = creditCardForm.expirationDate.value;
    const cardCvv = creditCardForm.cvv.value;

    const headers = addCorrId(new Headers({
      "Content-Type": "application/json",
      "X-Client-ID": malga.client_id,
      "X-Api-Key": malga.client_token,
    }));
    const holderInput = document.getElementById("card_holder_name");
    const cardHolderName = (holderInput && holderInput.value && holderInput.value.trim())
      ? holderInput.value.trim()
      : (formNameInput ? formNameInput.value.trim() : "");
    const body = JSON.stringify({
      cardHolderName,
      cardNumber,
      cardCvv,
      cardExpirationDate
    });

    return fetch(`${baseGatewayURL}/tokens`, { method: "post", headers, body })
      .then(tapAndLoadCorrId)
      .then(async (response) => {
        const bodyText = await response.text();
        if (!response.ok) {
          throw new Error(bodyText);
        }
        return JSON.parse(bodyText);
      })
      .then(async (cardTokenizationResponse) => {
        if (cardTokenizationResponse.error) {
          // Show error to your customer
          throw new Error(cardTokenizationResponse.error.message);
        }

        const cvvTokenizationResponse = tokenize_cvv && !isPJ(formClientType.value)
          ? await fetch(
            `${baseGatewayURL}/tokens`,
            { method: "post", headers, body: JSON.stringify({ cvvUpdate: cardCvv }) }
          )
            .then(tapAndLoadCorrId)
            .then(async (response) => {
              const bodyText = await response.text();
              if (!response.ok) {
                throw new Error(bodyText);
              }
              return JSON.parse(bodyText);
            })
          : {};
        gtm.registerCheckoutOptionEvent(
          PAYMENT_TYPE.card,
          {
            price: paymentHelpers.getTotalAmount(),
            discount: paymentHelpers.getDiscountAmount(),
            coupon: formCouponIdHiddenInput.value,
            productName: productNameHiddenInput.value,
            productId: productIdHiddenInput.value,
            fullName: formNameInput.value,
            email: formEmailInput.value,
            phone: formPhoneNumberInput.value
          }
        );

        // Otherwise send paymentMethod.id to your server
        fetchClientPayment(cardTokenizationResponse.tokenId, cvvTokenizationResponse?.tokenId);
      })
      .catch((error) => {
        const errorPayload = "message" in error ? (() => { try { return JSON.parse(error.message); } catch (_err) { return error; } })() : error;
        const declinedCode = "error" in errorPayload &&
          "declined_code" in errorPayload.error &&
          errorPayload.error.declined_code;

        setLoading(false);

        sendCardErrorToWebhook(declinedCode, error);
        console.error("Failed to tokenize card", error);

        switch (true) {
          case declinedCode === "invalid_expiration_date":
            showPaymentError("Data de validade do cartão de crédito inválida");
            break;
          case declinedCode === "invalid_number":
            showPaymentError("Número de cartão de crédito inválido");
            break;
          default:
            showPaymentError("Algo deu errado, por favor verifique os dados informados e tente novamente");
            Rollbar.error("Malga Tokenization error", error)
        }
      });
  };

  /**
   * Does the submit, please use `debouncedHandleSubmit` instead
   *
   * @deprecated
   */
  function submitCardPurchaseForm() {
    if (!formHelpers.cpfCnpjValidator()) {
      setLoading(false);
      return;
    }

    if (
      isPJ(formClientType.value) &&
      doesNotHaveValue(stateRegistrationInput) &&
      doesNotHaveValue(municipalRegistrationInput)
    ) {
      if (stateRegistrationAlertMessageElement) {
        stateRegistrationAlertMessageElement.className = "";
        stateRegistrationAlertMessageElement.style = "color:#FF5757; font-weight:600;";
      }
      setLoading(false);
      return;
    }

    if (stateRegistrationAlertMessageElement) {
      stateRegistrationAlertMessageElement.className = "hidden";
    }
    createPaymentMethodResponse();
  }

  const paymentMethodsSubmitHandlers = {
    card: submitCardPurchaseForm
  };

  function setPaymentMethodSubmitHandler(method, handler) {
    if (method in paymentMethodsSubmitHandlers) {
      console.error("Attempt to overwrite payment method handler already assigned:", method);
      return;
    }
    paymentMethodsSubmitHandlers[method] = handler;
  }

  const debouncedHandleSubmit = debounced(() => {
    const paymentMethod = submitButtonElement.dataset.paymentMethod;
    if (!(paymentMethod in paymentMethodsSubmitHandlers)) {
      console.error("Missing handler for payment method", paymentMethod);
      setLoading(false);
    }
    const handler = paymentMethodsSubmitHandlers[paymentMethod] ?? paymentMethodsSubmitHandlers.card;
    handler();
  }, 1000);

  formElement.addEventListener("submit", (event) => {
    event.preventDefault();
    setLoading(true);
    debouncedHandleSubmit();
  });

  submitButtonElement.dataset.paymentMethod = "card";

  function syncCCFieldsForPaymentMethod(paymentMethod) {
    const notUsingCardPaymentMethod = paymentMethod !== "card";
    disableRequiredInputField(creditCardForm.creditCardNumber._self, notUsingCardPaymentMethod, false);
    disableRequiredInputField(creditCardForm.expirationDate._self, notUsingCardPaymentMethod, false);
    disableRequiredInputField(creditCardForm.cvv._self, notUsingCardPaymentMethod, false);
  }

  MPMInstance?.addListener((paymentMethod) => {
    submitButtonElement.dataset.paymentMethod = paymentMethod;
    clientForm.setPersonalDocumentRequired(true);
    clientForm.setForeignerCheckboxHidden(paymentMethod === "nupay");
    clientForm.setPersonalPhoneNumberRequired(paymentMethod === "nupay");

    // NuPay requires a real CPF/CEP even if "estrangeiro" was checked on another tab.
    const desiredCpfCepDisabled = paymentMethod === "nupay" ? false : !!isForeignerInput?.checked;
    if (formDocumentInput && formDocumentInput.disabled !== desiredCpfCepDisabled) {
      formHelpers.disableCpfCnpjFields?.(desiredCpfCepDisabled);
    }
    if (formZipCodeInput && formZipCodeInput.disabled !== desiredCpfCepDisabled) {
      formHelpers.disableZipCodeField?.(desiredCpfCepDisabled);
    }

    syncCCFieldsForPaymentMethod(paymentMethod);

    const usingPixPaymentMethod = paymentMethod == 'pix';
    paymentTotalSpan.style.display = usingPixPaymentMethod ? 'none' : 'flex';
    pixTotalAmountSpan.style.display = usingPixPaymentMethod ? 'flex' : 'none';
  });

  // Sync CC fields for the initially active tab (listener only fires on click, not on load)
  const initialActiveTab = document.querySelector('.tab-trigger.active-tab');
  if (initialActiveTab) {
    syncCCFieldsForPaymentMethod(initialActiveTab.dataset.paymentMethod ?? 'card');
  }

  const loadingIndicator = submitButtonElement.querySelector(".loading-indicator");
  const submitButtonLabel = submitButtonElement.querySelector("span");

  function handlePaymentResult(redirectionUrl, label) {
    setLoading(false);
    disableSubmitButtonElement(submitButtonElement);

    setTimeout(() => {
      window.parent.location.replace(redirectionUrl);
    }, 2000);

    loadingIndicator.classList.remove("hidden");
    submitButtonLabel.innerText = label;
  }

  function getAuthenticityTokenElement() {
    const [authenticityTokenElement] = Array.from(
      document.getElementsByName("authenticity_token")
    );
    return authenticityTokenElement;
  }

  function prepareFieldsForPaymentMethods({ success, pending, error }) {
    const authenticityTokenElement = getAuthenticityTokenElement();
    const formElements = {
      authenticityTokenElement,
      stateRegistrationAlertMessageElement,
      submitButtonElement,
      submitButtonLabel,
      loadingIndicator,
      getProductInfo({ formatted = true } = {}) {
        return {
          productId: productIdHiddenInput.value,
          productName: productNameHiddenInput.value,
          unitAmount: formatted
            ? getFormattedCurrencyValueFromEl(formAmountHiddenInput)
            : getValueWithoutFormat(formAmountHiddenInput.value) / 100.0,
          pixAmount: formatted
            ? getFormattedCurrencyValueFromEl(formPixAmountHiddenInput)
            : getValueWithoutFormat(formPixAmountHiddenInput.value) / 100.0
        };
      },
      getAmountAndCouponInformation({ formatted = true } = {}) {
        return {
          coupon_id: formCouponIdHiddenInput.value,
          coupon_amount_off: formatted
            ? getFormattedCurrencyValueFromEl(
              formAmountOffHiddenInput
            )
            : formAmountOffHiddenInput.value / 100,
          coupon_percent_off: formPercentOffHiddenInput.value || +formPercentOffHiddenInput.value,
          discount_amount: formatted
            ? getFormattedCurrencyValueFromEl(
              formDiscountAmountHiddenInput
            )
            : formDiscountAmountHiddenInput.value / 100,
          subtotal_amount: formatted
            ? productUnitAmountHiddenInput?.innerText
            : getValueWithoutFormat(productUnitAmountHiddenInput?.innerText) / 100,
          total_amount: formatted
            ? getFormattedCurrencyValueFromEl(formTotalAmountHiddenInput)
            : +formTotalAmountHiddenInput.value / 100,
          pix_amount: formatted
            ? getFormattedCurrencyValueFromEl(formTotalPixAmountHiddenInput)
            : +formTotalPixAmountHiddenInput.value / 100
        };
      }
    };

    return {
      gtm,
      clientForm,
      productForm,
      couponForm,
      bumpForm,
      formHelpers,
      formElements,
      setLoading,
      showPaymentError,
      setPaymentMethodSubmitHandler,
      onSuccess() {
        handlePaymentResult(success.redirectURL, success.label);
      },
      onPending() {
        handlePaymentResult(pending.redirectURL, pending.label);
      },
      onError() {
        handlePaymentResult(error.redirectURL, error.label);
      }
    };
  }

  const hasPix = document.getElementById("pix-payment-form");
  const hasNupay = document.getElementById("nupay-payment-form");

  if (hasPix) {
    setupPixPayment(
      prepareFieldsForPaymentMethods({
        success: { label: "Pagamento feito com sucesso!", redirectURL: document.getElementById("typ_url").value },
        pending: {
          label: "Processando pagamento via Pix",
          redirectURL: document.getElementById("typ_pix_processing").value.trim() || window.PM3.typPixProcessing
        },
        error: {
          label: "Código Pix expirado",
          redirectURL: document.getElementById("typ_pix_error").value.trim() || window.PM3.typPixError
        }
      })
    );
  }
  if (hasNupay) {
    setupNupayPayment(
      prepareFieldsForPaymentMethods({
        success: { label: "Pagamento feito com sucesso!", redirectURL: document.getElementById("typ_url").value },
        pending: {
          label: "Processando pagamento via NuPay",
          redirectURL: document.getElementById("typ_pix_processing").value.trim() || window.PM3.typPixProcessing
        },
        error: {
          label: "Pagamento NuPay expirado",
          redirectURL: document.getElementById("typ_pix_error").value.trim() || window.PM3.typPixError
        }
      }),
    );
  }
}

document.addEventListener("DOMContentLoaded", function () {
  let MPMInstance;
  try {
    MPMInstance = setupMPMTabs(document.getElementById("mpm-tabs-contents"));
  } catch (err) {
    console.error("Failed to start MPM Tabs:", err);
  }

  try { setupMalgaPayment(MPMInstance); } catch (err) {
    console.error("Failed to setup Malga Payment:", err);
  }

  try {
    const nameInput = document.getElementById("client_payment_name") || document.querySelector('input[name="client_payment[name]"]');
    function syncCardHolderName() {
      const holderInput = document.getElementById("card_holder_name");
      if (!holderInput || !nameInput) return;
      holderInput.placeholder = (nameInput.value || "").trim();
    }
    if (nameInput) {
      nameInput.addEventListener("blur", syncCardHolderName);
    }
  } catch (err) {
    console.error("Failed to bind card holder name autofill:", err);
  }
});
